import { z } from 'zod';
/**
 * Corps des endpoints de transfert d'agence (devis et dossier). Un admin peut
 * réaffecter une affaire à l'autre agence à tout moment.
 */
export const updateAgencySchema = z.object({
    agency: z.enum(['MRS', 'RUN']),
});
/** Code IATA de l'aéroport Roland-Garros : la seule origine rattachée à l'agence Réunion. */
const REUNION_DEPARTURE_CODE = 'RUN';
/**
 * Expéditeur des mails transactionnels, par agence : un client doit recevoir ses
 * documents de l'agence qui suit son dossier, et pouvoir répondre directement au
 * bon interlocuteur — l'envoi pose donc ce couple à la fois en `from` et en
 * `reply-to` (cf. `BrevoService.sendTemplateEmail` : sur un envoi templaté, un
 * `reply-to` absent est repris du template, pas du `from`).
 *
 * Les deux adresses sont authentifiées (SPF/DKIM) côté Brevo. `MRS` reprend à
 * l'identique `BREVO_SENDER_EMAIL` / `BREVO_SENDER_NAME` — qui est aussi
 * l'expéditeur configuré sur *tous* les templates Brevo. Rien ne change donc
 * pour Marseille : seuls les envois Réunion basculent réellement d'adresse.
 *
 * Sert aussi de source aux coordonnées de contact affichées sur le site.
 */
// `as const satisfies` plutôt qu'une annotation : la forme reste vérifiée, mais
// les adresses gardent leur type littéral — ce qui permet d'en dériver l'enum
// zod qui verrouille l'expéditeur côté API.
export const AGENCY_SENDERS = {
    MRS: { email: 'contact@massiliavoyages.fr', name: 'Massilia Voyages' },
    RUN: { email: 'contact-reunion@massiliavoyages.fr', name: 'Massilia Voyages Réunion' },
};
/** Les seules adresses d'expédition acceptées : voir {@link agencyOfSenderEmail}. */
export const AGENCY_SENDER_EMAILS = [AGENCY_SENDERS.MRS.email, AGENCY_SENDERS.RUN.email];
/**
 * Agence correspondant à une adresse d'expédition, `undefined` si l'adresse
 * n'est pas l'une des nôtres.
 *
 * Les expéditeurs sont volontairement une liste fermée et non une saisie libre :
 * un `from` arbitraire accepté par l'API ferait du backoffice un relais
 * d'usurpation d'identité.
 */
export function agencyOfSenderEmail(email) {
    return Object.keys(AGENCY_SENDERS).find((agency) => AGENCY_SENDERS[agency].email === email);
}
/**
 * Heuristique historique de rattachement : un voyage au départ de La Réunion
 * relève de l'agence Réunion, tout le reste de Marseille.
 *
 * Elle ne sert plus qu'à *poser* l'agence (création côté client, backfill) :
 * une fois la colonne renseignée, c'est elle qui fait foi — un admin peut avoir
 * transféré le devis ou le dossier à l'autre agence.
 */
export function resolveAgencyFromDeparture(departureLocation) {
    return departureLocation === REUNION_DEPARTURE_CODE ? 'RUN' : 'MRS';
}
/**
 * Agence à poser sur un devis ou un dossier qui vient d'être créé.
 *
 * L'agence de l'admin qui crée l'emporte : c'est lui qui suivra l'affaire, quel
 * que soit le lieu de départ. À défaut d'admin (création autonome par un client
 * sur le site), on retombe sur {@link resolveAgencyFromDeparture}.
 */
export function resolveAgencyOnCreation({ adminAgency, departureLocation, }) {
    return adminAgency ?? resolveAgencyFromDeparture(departureLocation);
}
/**
 * Expéditeur d'un mail de dossier ou de devis : celui de l'agence propriétaire.
 */
export function resolveAgencySender(agency) {
    return AGENCY_SENDERS[agency];
}
